import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { HomePage } from './pages/HomePage';
import { EpisodePage } from './pages/EpisodePage';
import EpisodesPage from './pages/EpisodesPage';
import AboutPage from './pages/AboutPage';
import ToolsPage from './pages/ToolsPage';
import ExcuseGeneratorPage from './pages/ExcuseGeneratorPage';
import ClipboardPage from './pages/ClipboardPage';
import TutorialsPage from './pages/TutorialsPage';
import TutorialArticlePage from './pages/TutorialArticlePage';
import EthicsPage from './pages/EthicsPage';
import Footer from './components/Footer';
import { ScrollToTop } from './components/ScrollToTop';
import './styles.css';

// Header/Navigation Component
const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-sm border-b border-gray-800">
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" onClick={closeMobileMenu}>
            <img
              src="/operator-avatar.png"
              alt="The Operator logo"
              className="w-10 h-10 rounded-lg ring-2 ring-red-500/50 group-hover:ring-red-500 transition-all flex-shrink-0"
            />
            <div className="flex flex-col">
              <div className="text-sm sm:text-lg font-bold text-gray-100 group-hover:text-red-500 transition-colors leading-tight">
                AI Operator From Hell
              </div>
              <div className="text-[10px] sm:text-xs text-gray-500 font-mono">
                All fiction. All fun.
              </div>
            </div>
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/episodes" className="nav-link">Episodes</Link>
            <Link to="/tools" className="nav-link">Tools</Link>
            <Link to="/tutorials" className="nav-link">Tutorials</Link>
            <Link to="/about" className="nav-link">About</Link>
            <Link to="/ethics" className="nav-link">Ethics</Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden p-2 text-gray-400 hover:text-red-500 transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-800 pt-4 animate-fadeIn">
            {/* Mobile Menu Links */}
            <div className="flex flex-col space-y-4">
              <Link
                to="/episodes"
                className="text-gray-300 hover:text-red-500 transition-colors font-medium"
                onClick={closeMobileMenu}
              >
                Episodes
              </Link>
              <Link
                to="/tools"
                className="text-gray-300 hover:text-red-500 transition-colors font-medium"
                onClick={closeMobileMenu}
              >
                Tools
              </Link>
              <Link
                to="/tutorials"
                className="text-gray-300 hover:text-red-500 transition-colors font-medium"
                onClick={closeMobileMenu}
              >
                Tutorials
              </Link>
              <Link
                to="/about"
                className="text-gray-300 hover:text-red-500 transition-colors font-medium"
                onClick={closeMobileMenu}
              >
                About
              </Link>
              <Link
                to="/ethics"
                className="text-gray-300 hover:text-red-500 transition-colors font-medium"
                onClick={closeMobileMenu}
              >
                Ethics
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

// Main App Component with Router
const App: React.FC = () => {
  return (
    <Router>
      <ScrollToTop />
      <div className="app">
        <Header />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/episodes" element={<EpisodesPage />} />
          <Route path="/episodes/:slug" element={<EpisodePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/tools" element={<ToolsPage />} />
          <Route path="/tools/excuse-generator" element={<ExcuseGeneratorPage />} />
          <Route path="/tools/clipboard" element={<ClipboardPage />} />
          <Route path="/tutorials" element={<TutorialsPage />} />
          <Route path="/tutorials/:slug" element={<TutorialArticlePage />} />
          <Route path="/ethics" element={<EthicsPage />} />
        </Routes>
        <Footer />
      </div>
    </Router>
  );
};

export default App;
